import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { SelectableItem, SelectableList } from '../../../components/SelectableList';
import { Tabs } from '../../../components/Tabs';
import Toast from 'react-native-toast-message';
import { accessService } from '../../../services/central-api/access';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

interface ModalProps {
    isModalVisible: boolean;
    onClose: (success: boolean) => void;
    turnstileId?: number;
}

export default function TurnstileModal({ onClose, turnstileId, isModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState('');
    const [selectableUsers, setSelectableUsers] = useState<SelectableItem[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<SelectableItem[]>([]);

    const [turnstilesUserIds, setTurnstilesUserIds] = useState<number[]>([]);

    async function getTurnstile(id: number) {
        const turnstile = await accessService.getHikTurnstile(id);
        setName(turnstile.name);

        const users = await loadUsers();
        const selectedUsers = turnstile.Users.map((user) => {
            return {
                id: user.id || 0,
                name: user.name
            };
        });
        setSelectedUsers(selectedUsers);

        setSelectableUsers((users || [])
            .filter((user) => !selectedUsers.find((turnstileUser) => turnstileUser.id == user.id))
            .map((user) => {
                return {
                    id: user.id || 0,
                    name: user.name
                };
            })
        );

        setTurnstilesUserIds(turnstile.Users.map((user) => user.id || 0));
    }

    async function loadPageInfo() {
        try {
            if (turnstileId) {
                await getTurnstile(turnstileId);
            } else {
                loadUsers();
            }
        } catch (err) {
            console.error(err);
        }
    }

    async function loadUsers() {
        try {
            setSelectedUsers([]);
            const users = await accessService.getAccessAllUsers({});
            setSelectableUsers(users.map((user) => {
                return {
                    id: user.id || 0,
                    name: user.name
                };
            }));
            return users;
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    function isEmpty(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isEmpty(name);
    }

    async function formSubmit() {
        try {
            if (turnstileId) {
                const selectedUserIds = selectedUsers.map((user) => Number(user.id));
                const usersToAdd = selectedUserIds.filter((selectedUserId) => !turnstilesUserIds.includes(selectedUserId));
                const usersToRm = turnstilesUserIds.filter((turnstileUserIds) => !selectedUserIds.includes(turnstileUserIds));


                await accessService.updateHikTurnstile(turnstileId, {
                    usersToAdd,
                    usersToRm,
                });
                onClose(true);
                return;
            }
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose(false);
            }}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 635 }]}>
                    <Tabs tabs={[{
                        key: 'details',
                        label: translate('details')
                    }, {
                        key: 'users',
                        label: translate('users')
                    }]}>
                        {({ selectedTab }) => {
                            if (selectedTab == 'details') {
                                return (<>
                                    <FormInput
                                        label={translate('name')}
                                        placeholder={translate('name')}
                                        value={name}
                                        onChangeText={setName}
                                        invalid={isEmpty}
                                        disabled={true}
                                    />
                                </>);
                            }
                            if (selectedTab == 'users') {
                                return <SelectableList
                                    selectableLabel={translate('users')}
                                    selectableContent={selectableUsers}
                                    selectedLabel={translate('participants')}
                                    selectedContent={selectedUsers}
                                />;
                            }
                        }}
                    </Tabs>
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => onClose(false)}
                        disabled={!isFormValid()} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        height: 560,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        justifyContent: 'space-between',
        rowGap: 10
    },
}));
